"use client";

/**
 * SceneHookHero — unified display-serif scene hook.
 *
 * Single source of truth for Plan v2, Brand v2, Niche v3.
 *
 * Promoted (Session 43) from forks:
 *   - PlanShared.SceneHookHero    → API: { text, size: "lg"|"xl" }, single fade-up, no stagger.
 *   - niche-v3/.../SceneHookHero  → API: { children, gradient, stagger }, word-by-word stagger.
 *
 * Unified API supports BOTH:
 *   - Niche v3: `<SceneHookHero gradient stagger>{content}</SceneHookHero>`
 *   - Plan/Brand legacy: `<SceneHookHero text="..." size="lg" />` — `text` becomes content,
 *     legacy `size` selects responsive Tailwind class.
 *
 * Stagger requires `children` (or `text`) to be a string. Mixed React children fall back to
 * container-level fade-up even with stagger=true.
 *
 * Reduced motion: collapses to single fade-in.
 */

import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { cn } from "@/lib/utils";

export type SceneHookHeroSize = "lg" | "xl";

interface SceneHookHeroProps {
  /** Hook content (preferred — Niche v3 API). Either string or composed nodes. */
  children?: ReactNode;
  /** Legacy alias for children (Plan/Brand) — string only. If both, `children` wins. */
  text?: string;
  /** Extra classes (override sizing/alignment). */
  className?: string;
  /** Apply amber→white gradient text treatment. Default true. */
  gradient?: boolean;
  /** Word-by-word stagger reveal (40ms/word). Requires string content. Default false. */
  stagger?: boolean;
  /**
   * Legacy Plan/Brand size token. If provided, overrides default responsive sizing.
   *   "lg" → text-2xl md:text-4xl lg:text-5xl
   *   "xl" → text-3xl md:text-5xl lg:text-6xl
   * Niche v3 default (no `size`): text-[32px] sm:text-4xl md:text-5xl lg:text-6xl
   */
  size?: SceneHookHeroSize;
  /** Heading semantic — default `p`. */
  as?: "h1" | "h2" | "h3" | "p" | "div";
}

function sizeClass(size: SceneHookHeroSize | undefined): string {
  if (size === "xl") return "text-3xl md:text-5xl lg:text-6xl";
  if (size === "lg") return "text-2xl md:text-4xl lg:text-5xl";
  // Niche v3 default — slightly larger mobile baseline.
  return "text-[32px] sm:text-4xl md:text-5xl lg:text-6xl";
}

export function SceneHookHero({
  children,
  text,
  className,
  gradient = true,
  stagger = false,
  size,
  as: Tag = "p",
}: SceneHookHeroProps) {
  const reduced = useReducedMotion();

  // Resolve content: explicit children wins, else legacy `text`.
  const content: ReactNode = children ?? text ?? null;

  const baseClasses = cn(
    "max-w-3xl font-heading font-bold tracking-tight leading-[1.15]",
    sizeClass(size),
    gradient
      ? "bg-gradient-to-r from-amber-300 via-amber-200 to-white bg-clip-text text-transparent"
      : "text-white",
    className,
  );

  const canStagger = stagger && typeof content === "string";
  const words = useMemo(() => {
    if (!canStagger) return null;
    return (content as string).split(/(\s+)/);
  }, [canStagger, content]);

  // Use motion[Tag] for both modes so `as` works generically.
  const MotionTag = motion[Tag];

  // Reduced-motion OR no stagger → single container fade-up.
  if (!canStagger || reduced) {
    return (
      <MotionTag
        initial={reduced ? false : { opacity: 0, y: 16 }}
        whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: reduced ? 0 : 0.6, ease: "easeOut" }}
        className={baseClasses}
      >
        {content}
      </MotionTag>
    );
  }

  // Stagger mode: split on whitespace, animate each word with 40ms delay.
  const aria = typeof content === "string" ? content : undefined;
  // Headings (h1/h2/h3) accept aria-label natively via their implicit role.
  // `p` / `div` would violate ARIA 1.2 `aria-prohibited-attr` (paragraph /
  // generic element cannot carry aria-label). Those get an sr-only twin
  // instead so SR still reads the full line up-front.
  const headingTag = Tag === "h1" || Tag === "h2" || Tag === "h3";
  return (
    <>
      {!headingTag && aria ? (
        <span className="sr-only">{aria}</span>
      ) : null}
      <MotionTag
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-60px" }}
        className={baseClasses}
        aria-label={headingTag ? aria : undefined}
        aria-hidden={!headingTag ? true : undefined}
      >
      {words!.map((token, i) => {
        if (/^\s+$/.test(token)) {
          return (
            <span key={i} aria-hidden>
              {token}
            </span>
          );
        }
        return (
          <motion.span
            key={i}
            aria-hidden
            variants={{
              hidden: { opacity: 0, y: 14 },
              visible: { opacity: 1, y: 0 },
            }}
            transition={{
              duration: 0.45,
              ease: "easeOut",
              delay: (i * 40) / 1000,
            }}
            className="inline-block"
          >
            {token}
          </motion.span>
        );
      })}
      </MotionTag>
    </>
  );
}
